import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaChevronLeft,
  FaChevronRight,
  FaGift,
  FaHeart,
  FaLayerGroup,
  FaMagic,
  FaRedoAlt,
  FaRegStar,
} from 'react-icons/fa';
import { getOpeningRevealPreset } from '../../services/openingPresets';
import { getExperienceLine, getModuleTitle, getProgressPercent, nudge, playSoftTone } from './openingUtils';
import { getThemeOpeningConfig } from './themeOpeningConfig';
import { ThemeObject, getThemeOpeningStyle } from './ThemeOpeningObjects';
import { getLayeredGiftCoverMedia } from './layeredGift/coverMediaSelector';
import ModuleRevealCard from './layeredGift/ModuleRevealCard';
import { getRevealMeta } from './layeredGift/moduleRevealMeta';
import './layeredGift/LayeredGiftReveal.css';

const LAYER_PROMISES = ['Reveal', 'One chapter', 'Next layer'];

const shouldShowInLayeredGift = (module) => module?.data?.showInLayeredGift !== false;

const LayeredGiftExperience = ({ modules, renderModules, openingMode, giftIntro = {}, onComplete, startAtFirstLayer = false }) => {
  const layers = (Array.isArray(modules) ? modules : []).filter(shouldShowInLayeredGift);
  const initialLayerIndex = startAtFirstLayer ? 0 : -1;
  const [activeIndex, setActiveIndex] = useState(initialLayerIndex);
  const [boxOpen, setBoxOpen] = useState(false);
  const [openedLayers, setOpenedLayers] = useState({});

  useEffect(() => {
    setActiveIndex(initialLayerIndex);
    setBoxOpen(false);
    setOpenedLayers({});
  }, [openingMode, layers.length, initialLayerIndex]);

  const recipientName = giftIntro.ownerName || 'you';
  const activeModule = layers[activeIndex];
  const activeLayerKey = activeModule ? `${activeModule.id || activeModule._localId || activeModule.type}_${activeIndex}` : '';
  const activeRevealMeta = getRevealMeta(activeModule, activeIndex, {
    recipientName,
    pageId: giftIntro.pageId,
    themeId: giftIntro.themeId,
    isFinalLayer: activeIndex === layers.length - 1,
  });
  const isLayerOpen = Boolean(activeLayerKey && openedLayers[activeLayerKey]);
  const hasStarted = activeIndex >= 0;
  const isDone = layers.length > 0 && activeIndex >= layers.length;
  const progress = getProgressPercent(activeIndex, layers.length);
  const introTitle = giftIntro.title || `For ${recipientName}`;
  const introCopy = giftIntro.subtitle ||
    'Open the gift one layer at a time. Each tap reveals one private chapter before the next appears.';
  const preset = getOpeningRevealPreset(giftIntro.themeId);
  const coverMedia = getLayeredGiftCoverMedia({
    pageData: giftIntro.pageData || giftIntro,
    modules: layers,
    fallbackImage: preset.image,
  });
  const coverImage = coverMedia.url;
  const coverCrop = coverMedia.crop || { x: 0, y: 0, zoom: 1 };
  const coverCropStyle = {
    transform: `translate(-50%, -50%) translate(${coverCrop.x || 0}%, ${coverCrop.y || 0}%) scale(${coverCrop.zoom || 1})`,
  };
  const themeOpeningConfig = getThemeOpeningConfig(giftIntro.themeId);
  const coverLabel = themeOpeningConfig?.label || preset.label;
  const coverMaterial = themeOpeningConfig?.material || preset.material;
  const coverAction = themeOpeningConfig?.action || 'Open the gift';
  const layerCountLabel = layers.length ? `${layers.length} guided layers` : 'Guided layers';

  const openBox = () => {
    nudge([20, 30, 20]);
    playSoftTone(523, 0.1);
    setBoxOpen(true);
    setTimeout(() => {
      playSoftTone(784, 0.14);
      setActiveIndex(0);
    }, 900);
  };

  const previousLayer = () => {
    nudge(12);
    setActiveIndex((value) => Math.max(-1, value - 1));
  };

  const openCurrentLayer = () => {
    if (!activeLayerKey) return;
    nudge([12, 22, 12]);
    playSoftTone(680, 0.1);
    setOpenedLayers((value) => ({
      ...value,
      [activeLayerKey]: true,
    }));
  };

  const nextLayer = () => {
    if (!isLayerOpen) {
      openCurrentLayer();
      return;
    }
    nudge(18);
    playSoftTone(activeIndex === layers.length - 1 ? 880 : 660, 0.1);
    if (activeIndex === layers.length - 1) {
      onComplete?.();
    }
    setActiveIndex((value) => value + 1);
  };

  return (
    <div className="w-full max-w-[430px] mx-auto px-0 space-y-6">
      <AnimatePresence mode="wait">
        {!hasStarted && (
          <motion.button
            key="gift-cover"
            onClick={openBox}
            initial={{ opacity: 0, scale: 0.92, rotate: -1 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 1.08, rotate: 1 }}
            className="lumirae-opening-card lumirae-opening-reveal-card lumirae-theme-opening lumirae-guided-layers-cover relative mx-2 min-h-[660px] w-[calc(100%-1rem)] p-0"
            data-open={boxOpen ? 'true' : 'false'}
            data-theme-opening={themeOpeningConfig?.variant || 'guided-gift'}
            data-theme-id={giftIntro.themeId || ''}
            data-cover-source={coverMedia.source || 'themeFallback'}
            style={themeOpeningConfig ? getThemeOpeningStyle(themeOpeningConfig.colors) : undefined}
          >
            <motion.div
              className="lumirae-opening-reveal-photo"
              animate={{ scale: boxOpen ? 1.07 : 1 }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
            >
              {coverImage && (
                <img
                  src={coverImage}
                  alt=""
                  draggable="false"
                  style={coverCropStyle}
                />
              )}
            </motion.div>
            <div className="lumirae-opening-reveal-scrim" />
            <div className="lumirae-opening-theme-frame" aria-hidden="true">
              <span className="frame-line top" />
              <span className="frame-line bottom" />
              <span className="theme-mark mark-a" />
              <span className="theme-mark mark-b" />
            </div>

            <div className="lumirae-opening-stage lumirae-opening-reveal-stage flex h-full min-h-[660px] flex-col text-center [perspective:1100px]">
              <div className="lumirae-opening-reveal-top">
                <div className="lumirae-opening-reveal-brand">Lumirae</div>
                <div className="lumirae-opening-reveal-chip">
                  <FaLayerGroup />
                  {coverLabel}
                </div>
              </div>

              <div className="lumirae-opening-reveal-copy">
                <div className="lumirae-opening-label">
                  {coverMaterial} / {layerCountLabel}
                </div>
                <h2 className="lumirae-opening-title">
                  {boxOpen ? 'Opening...' : introTitle}
                </h2>
                <p className="lumirae-opening-copy">
                  {introCopy}
                </p>
              </div>

              {themeOpeningConfig ? (
                <ThemeObject variant={themeOpeningConfig.variant} opened={boxOpen} />
              ) : (
                <div className="lumirae-opening-reveal-object [transform-style:preserve-3d]">
                  <motion.div
                    className="lumirae-opening-reveal-card-back"
                    animate={boxOpen ? { rotateX: 7, y: 18, scale: 0.98 } : { rotateX: [0, 2, 0] }}
                    transition={{ duration: boxOpen ? 0.7 : 3, repeat: boxOpen ? 0 : Infinity, ease: 'easeInOut' }}
                  />
                  <motion.div
                    className="lumirae-opening-reveal-card-lid"
                    animate={boxOpen ? { rotateX: -118, y: -30, z: 38 } : { rotateX: [0, -4, 0] }}
                    transition={{ duration: boxOpen ? 0.95 : 2.6, repeat: boxOpen ? 0 : Infinity, ease: 'easeInOut' }}
                  />
                  <motion.div
                    className="lumirae-opening-reveal-ribbon vertical"
                    animate={{ scaleY: boxOpen ? 0.84 : 1, opacity: boxOpen ? 0.72 : 1 }}
                  />
                  <motion.div
                    className="lumirae-opening-reveal-ribbon horizontal"
                    animate={{ scaleX: boxOpen ? 0.9 : 1, opacity: boxOpen ? 0.72 : 1 }}
                  />
                  {boxOpen && [0, 1, 2, 3, 4, 5].map((piece) => (
                    <motion.span
                      key={piece}
                      className="lumirae-opening-reveal-spark"
                      initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                      animate={{ opacity: [0, 1, 0], x: (piece - 2.5) * 30, y: -80 - (piece % 2) * 28, scale: 1 }}
                      transition={{ duration: 0.9, delay: piece * 0.04 }}
                    />
                  ))}
                  {boxOpen && (
                    <motion.div
                      className="lumirae-opening-reveal-inner-card"
                      initial={{ opacity: 0, y: 34, rotateX: 58, scale: 0.82 }}
                      animate={{ opacity: 1, y: -34, rotateX: 0, scale: 1 }}
                      transition={{ delay: 0.15, duration: 0.65, ease: 'easeOut' }}
                    >
                      <div>
                        <span>First layer</span>
                        <strong>{layers[0] ? getModuleTitle(layers[0], 0) : 'A memory'}</strong>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              <div className="lumirae-opening-reveal-steps lumirae-guided-layer-promises">
                {LAYER_PROMISES.map((step, index) => (
                  <motion.div
                    key={step}
                    className="lumirae-opening-reveal-step"
                    animate={boxOpen && index <= 2 ? { y: [0, -4, 0], opacity: [0.64, 1, 0.72] } : { opacity: 0.72 }}
                    transition={{ duration: 0.55, delay: index * 0.16 }}
                  >
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <strong>{step}</strong>
                  </motion.div>
                ))}
              </div>

              <div className="lumirae-opening-reveal-action">
                <div className="lumirae-opening-pill lumirae-guided-layer-cta inline-flex items-center gap-2 px-5 py-3 text-[9px] font-black uppercase tracking-[0.25em]">
                  <FaMagic />
                  {boxOpen ? 'Preparing the first layer' : coverAction}
                </div>
                <div className="lumirae-guided-layer-hint">
                  Tap once. The story opens slowly.
                </div>
              </div>
            </div>
          </motion.button>
        )}

        {hasStarted && !isDone && activeModule && (
          <motion.div
            key={activeModule.id || activeModule._localId || activeIndex}
            initial={{ opacity: 0, y: 42, scale: 0.94, rotateX: -7 }}
            animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
            exit={{ opacity: 0, y: -24, scale: 0.94, rotateX: 8 }}
            className="lumirae-guided-layer-reader w-full space-y-5 overflow-visible"
          >
            <div className="lumirae-opening-card lumirae-guided-layer-nav mx-4 p-4">
              <div className="lumirae-guided-layer-nav-top">
                <div>
                  <div className="lumirae-opening-label">
                    Layer {activeIndex + 1} of {layers.length} / {activeRevealMeta.kicker}
                  </div>
                  <h2>{getModuleTitle(activeModule, activeIndex)}</h2>
                </div>
                <div className="lumirae-guided-layer-token">
                  <FaGift />
                </div>
              </div>

              <div className="lumirae-opening-progress my-4 h-2">
                <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${progress}%` }} />
              </div>

              <div className="lumirae-guided-layer-controls">
                <button
                  onClick={previousLayer}
                  className="lumirae-opening-button-secondary"
                  aria-label="Previous layer"
                >
                  <FaChevronLeft />
                  Back
                </button>
                <div className="lumirae-guided-layer-microcopy">
                  <FaHeart />
                  <span>{getExperienceLine(activeIndex)}</span>
                </div>
                <button onClick={nextLayer} className="lumirae-opening-button">
                  {!isLayerOpen ? activeRevealMeta.action : (activeIndex === layers.length - 1 ? 'Finish' : 'Next')}
                  <FaChevronRight />
                </button>
              </div>

            </div>

            <div className="lumirae-guided-layer-card-shell">
              <div className="lumirae-guided-layer-card-stack" aria-hidden="true">
                <span />
                <span />
              </div>
              <AnimatePresence mode="wait">
                {!isLayerOpen ? (
                  <ModuleRevealCard
                    key={`reveal_${activeLayerKey}`}
                    layerKey={activeLayerKey}
                    revealMeta={activeRevealMeta}
                    onOpen={openCurrentLayer}
                  />
                ) : (
                  <motion.div
                    key={`module_${activeLayerKey}`}
                    className="relative"
                    initial={{ opacity: 0, y: 34, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -18, scale: 0.98 }}
                  >
                    <div className="absolute inset-x-10 -top-4 h-8 rounded-full blur-xl" style={{ backgroundColor: 'color-mix(in srgb, var(--lumirae-accent-soft) 70%, transparent)' }} />
                    {renderModules([activeModule], 'layered_gift')}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {isDone && (
          <motion.div
            key="gift-done"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            className="lumirae-opening-card lumirae-guided-layer-complete mx-4 p-10 text-center"
          >
            <div className="lumirae-guided-layer-complete-mark">
              <FaRegStar />
            </div>
            <div className="lumirae-opening-label">Guided gift complete</div>
            <h2 className="lumirae-opening-title text-3xl italic">All Layers Opened</h2>
            <p className="lumirae-opening-copy mx-auto mt-3 max-w-xs">
              Every chapter has been opened in order. The gift now feels complete, personal, and ready to revisit anytime.
            </p>
            <div className="lumirae-guided-layer-complete-actions">
              <button
                onClick={() => setActiveIndex(0)}
                className="lumirae-opening-button px-8 py-4 text-[10px] font-black uppercase tracking-widest"
              >
                <FaRedoAlt />
                Open Again
              </button>
              <button
                onClick={() => setActiveIndex(-1)}
                className="lumirae-opening-button-secondary px-8 py-4 text-[10px] font-black uppercase tracking-widest"
              >
                Cover
              </button>
            </div>
          </motion.div>
        )}

        {hasStarted && !isDone && !activeModule && (
          <motion.div
            key="empty-layered-gift"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            className="lumirae-opening-card lumirae-guided-layer-complete mx-4 p-10 text-center"
          >
            <div className="lumirae-guided-layer-complete-mark">
              <FaGift />
            </div>
            <h2 className="lumirae-opening-title text-3xl italic">No Layers Yet</h2>
            <p className="lumirae-opening-copy mx-auto mt-3 max-w-xs">
              Add one chapter in the builder and this gift will open layer by layer.
            </p>
            <button
              onClick={() => setActiveIndex(-1)}
              className="lumirae-opening-button mt-7 px-8 py-4 text-[10px] font-black uppercase tracking-widest"
            >
              Back to Cover
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LayeredGiftExperience;
